/**
 * Nucleo dell'engine di configurazione Motornet: appiattimento degli accessori,
 * risoluzione delle formule (vincoli / inclusioni / esclusioni), applicazione
 * delle "catene" e calcolo dei prezzi.
 *
 * Terminologia:
 * - **accessorio di serie**: dotazione già inclusa nel prezzo del veicolo;
 * - **vincolo**: accessorio che la selezione *obbliga* a trascinare con sé;
 * - **inclusione**: accessorio *incluso a costo zero* dalla selezione (es. membri di un pacchetto);
 * - **esclusione**: accessorio reso *incompatibile* dalla selezione;
 * - **formula**: albero booleano (nodi AND/OR) che descrive quali accessori
 *   soddisfano un vincolo/inclusione/esclusione;
 * - **catena**: l'insieme di accessori applicati a partire da una scelta
 *   dell'utente (il "capostipite"/root) e dai suoi compagni;
 * - **pick**: scelta esplicita dell'utente, da cui l'engine ricostruisce il resto.
 */
import type {
	MotornetAccessoriesResponse,
	MotornetAccessory,
	MotornetAccessoryKind,
	MotornetFormula,
	MotornetTipoVincolo,
	MotornetVincolo,
} from 'src/motornet/types';

/** Tipo/"secchio" di un accessorio: serie, optional, vernici, pacchetti, ... */
export type MotornetAccessoryBucket = MotornetAccessoryKind;

/** I bucket effettivamente mostrati in UI (gli altri restano solo di supporto). */
export const RENDERED_BUCKETS: readonly MotornetAccessoryBucket[] = [
	'serie',
	'optionals',
	'vernici',
	'pacchetti',
];

/**
 * Accessorio Motornet arricchito con lo stato di lavoro dell'engine.
 * I campi extra descrivono come l'accessorio partecipa alla configurazione:
 * - `selected`: fa parte della configurazione corrente;
 * - `tipoVincolo` / `tipoVincoloArray`: effetto complessivo e per-target
 *   (id → vincolo/inclusione/esclusione) generato da questo accessorio;
 * - `appliedBy` / `appliedKind`: se è stato trascinato da una catena, chi
 *   (id del root) e con quale legame (vincolo o inclusione);
 * - `prezzoDaVincolo` / `prezzoDaVincoloNetto`: prezzo alternativo imposto dal
 *   vincolo che lo ha attivato (sostituisce il listino proprio dell'accessorio).
 */
export interface WorkingAccessory extends MotornetAccessory {
	tipoAccessorio: MotornetAccessoryBucket;
	selected?: boolean;
	tipoVincolo?: MotornetTipoVincolo;
	tipoVincoloArray?: Record<string, MotornetTipoVincolo>;
	appliedBy?: number;
	appliedKind?: 'vincolo' | 'inclusione';
	prezzoDaVincolo?: number;
	prezzoDaVincoloNetto?: number;
}

/** Risultato completo di una configurazione, pronto per la UI e i totali. */
export interface ConfigurationResult {
	accessories: WorkingAccessory[];
	selected: WorkingAccessory[];
	disabledIds: number[];
	totalAccessoriesPriceListino: number;
	totalAccessoriesPrice: number;
	totalVehiclePrice: number;
	counts: Record<MotornetAccessoryBucket, number>;
	byMacrogruppo: Record<MotornetAccessoryBucket, Record<string, WorkingAccessory[]>>;
}

/**
 * Scelta dell'utente per un nodo OR di una formula: per l'accessorio
 * `accessoryId`, nel legame `kind`, ha scelto il ramo identificato da `chosenId`.
 */
export interface OrChoice {
	accessoryId: number;
	kind: 'vincolo' | 'inclusione' | 'esclusione';
	chosenId: number;
}

/**
 * Un nodo OR di una formula che richiede (o porta) una scelta dell'utente:
 * è la "domanda" (Q&A) mostrata quando un vincolo/inclusione/esclusione ammette
 * più alternative.
 */
export interface FormulaOrGroup {
	accessoryId: number;
	kind: OrChoice['kind'];
	/** Identità stabile del nodo OR all'interno del suo albero-formula. */
	key: string;
	branches: FormulaOrBranch[];
	/** Tutti gli id di accessorio raggiungibili sotto questo nodo OR (per la pulizia delle scelte). */
	allIds: number[];
	/** La scelta corrente dell'utente, se presente. Mai impostata di default. */
	chosenId?: number;
}

/** Un ramo (alternativa) di un {@link FormulaOrGroup}. */
export interface FormulaOrBranch {
	/** L'id da registrare come `OrChoice.chosenId` quando si sceglie questo ramo. */
	chosenId: number;
	/** Accessori a cui questo ramo si risolve (dati gli attuali sotto-choice annidati). */
	ids: number[];
}

/** Operatore di un nodo-formula: disgiunzione (una alternativa fra tante). */
export const OR = 'OR' as const;
/** Operatore di un nodo-formula: congiunzione (tutti gli elementi insieme). */
export const AND = 'AND' as const;

/** Aliquota IVA di ripiego, usata per derivare il netto quando manca il rapporto reale. */
const IVA_FALLBACK = 22;

/** Tutti i bucket di accessori presenti nel payload, nell'ordine di appiattimento. */
export const ACCESSORY_BUCKETS: MotornetAccessoryBucket[] = [
	'serie',
	'optionals',
	'vernici',
	'pacchetti',
	'accessoriVinNonCorrispondenti',
];

/**
 * Appiattisce i bucket del payload Motornet in un'unica lista di
 * {@link WorkingAccessory}, marcando ciascuno con il proprio `tipoAccessorio`.
 */
export function flattenAccessories(
	response: MotornetAccessoriesResponse
): WorkingAccessory[] {
	const result: WorkingAccessory[] = [];

	for (const tipoAccessorio of ACCESSORY_BUCKETS) {
		const items = response[tipoAccessorio] ?? [];
		for (const accessory of items) {
			result.push({
				...accessory,
				tipoAccessorio,
			});
		}
	}

	return result;
}

/** Raggruppa gli accessori per `macrogruppo` (categoria merceologica Motornet). */
export function groupByMacrogruppo(
	accessories: WorkingAccessory[]
): Record<string, WorkingAccessory[]> {
	const groups: Record<string, WorkingAccessory[]> = {};
	for (const accessory of accessories) {
		const key = accessory.macrogruppo;
		(groups[key] ??= []).push(accessory);
	}
	return groups;
}

/**
 * Etichetta principale da mostrare per un accessorio: la sorgente cambia in
 * base al bucket (Motornet popola i vari campi descrittivi in modo disomogeneo).
 */
export function primaryLabel(accessory: WorkingAccessory): string {
	const { tipoAccessorio } = accessory;
	const descrNormalizzata = accessory.descrizioneNormalizzata ?? '';
	const codiceNormalizzato = accessory.codiceNormalizzato ?? '';
	const descrizioneBreve = accessory.descrizioneBreve ?? '';
	const descrizione = accessory.descrizione ?? '';

	if (
		tipoAccessorio === 'optionals' &&
		(!codiceNormalizzato || codiceNormalizzato === '0000')
	) {
		return '';
	}
	if (tipoAccessorio === 'vernici') {
		return descrizione;
	}
	if (
		tipoAccessorio === 'pacchetti' &&
		(!descrizioneBreve || descrizioneBreve.trim().length > 3)
	) {
		return descrizione;
	}
	return descrNormalizzata;
}

/** Etichetta secondaria (sottotitolo) di un accessorio, dipendente dal bucket. */
export function secondaryLabel(accessory: WorkingAccessory): string {
	const { tipoAccessorio } = accessory;
	const descrizione = accessory.descrizione ?? '';
	const descrizioneBreve = accessory.descrizioneBreve ?? '';

	if (tipoAccessorio === 'optionals') return descrizione;
	if (
		(tipoAccessorio === 'pacchetti' &&
			(!descrizioneBreve || descrizioneBreve.trim().length > 3)) ||
		tipoAccessorio === 'vernici'
	) {
		return descrizioneBreve;
	}
	return descrizione;
}

/** Testo del tooltip informativo di un accessorio, se previsto per il suo bucket. */
export function infoTooltip(accessory: WorkingAccessory): string | null {
	const { tipoAccessorio, descrizioneBreve } = accessory;
	if (tipoAccessorio === 'optionals') {
		return !descrizioneBreve || descrizioneBreve.trim().length > 3
			? descrizioneBreve
			: null;
	}
	return null;
}

/**
 * Trova gli accessori corrispondenti a una lista di `idDizionario`. Le formule
 * Motornet referenziano gli accessori per id di dizionario, non per idAccessorio.
 */
export function findByDizionarioIds(
	all: WorkingAccessory[],
	ids: number[]
): WorkingAccessory[] {
	if (!ids.length) return [];
	const wanted = new Set(ids);
	return all.filter((a) => wanted.has(a.idDizionario));
}

/** Un ramo di un nodo OR: o un accessorio-foglia (`member`) o un sotto-albero (`node`). */
type OrBranchRef =
	| { member: WorkingAccessory }
	| { node: MotornetFormula };

/** Elenca i rami di un nodo OR: gli accessori-foglia diretti più i sotto-nodi. */
function orBranchesOf(
	node: MotornetFormula,
	all: WorkingAccessory[]
): OrBranchRef[] {
	const members = findByDizionarioIds(all, node.ids ?? []);
	return [
		...members.map((member) => ({ member })),
		...(node.operations ?? []).map((child) => ({ node: child })),
	];
}

/** Tutti gli `idDizionario` presenti nel sotto-albero di un nodo (ricorsivo). */
function subtreeDizionarioIds(node: MotornetFormula): number[] {
	const out = [...(node.ids ?? [])];
	for (const child of node.operations ?? []) {
		out.push(...subtreeDizionarioIds(child));
	}
	return out;
}

/** Tutti gli `idDizionario` referenziati da una formula (ricorsivo, null-safe). */
export function formulaDizionarioIds(
	node: MotornetFormula | null | undefined
): number[] {
	return node ? subtreeDizionarioIds(node) : [];
}

/**
 * Una coppia di vernici "sempre insieme": Motornet rappresenta un colore con due
 * accessori, il `master` (che porta il prezzo) e il `twin`/gemello tecnico
 * (prezzo 0). Vanno mostrati come un'unica voce.
 */
export interface BondedColourPair {
	master: WorkingAccessory;
	twin: WorkingAccessory;
}

/**
 * Individua le coppie di vernici legate in modo reciproco. Il legame è
 * bidirezionale e si riconosce — a prescindere dal prezzo — dall'anello fra le
 * formule, espresse per `idDizionario`:
 *   - `master.formulaInclusione` contiene l'`idDizionario` del gemello;
 *   - `twin.formulaVincoli` contiene l'`idDizionario` del master.
 *
 * Il membro che *possiede* il vincolo reciproco è il gemello; l'altro è il
 * master. La reciprocità è la garanzia: i master non hanno `formulaVincoli`, per
 * cui un colore incluso ma senza vincolo di ritorno non produce mai una coppia.
 */
export function findBondedColourPairs(
	colours: WorkingAccessory[]
): BondedColourPair[] {
	const byDizionario = new Map<number, WorkingAccessory>();
	for (const c of colours) byDizionario.set(c.idDizionario, c);

	const pairs: BondedColourPair[] = [];
	const takenTwins = new Set<number>();

	for (const master of colours) {
		for (const dizId of formulaDizionarioIds(
			master.formulaInclusioneStrutturata
		)) {
			const twin = byDizionario.get(dizId);
			if (!twin || twin.idAccessorio === master.idAccessorio) continue;
			if (takenTwins.has(twin.idAccessorio)) continue;

			const twinBindsBack = (twin.formulaVincoli ?? []).some((v) =>
				formulaDizionarioIds(v.formulaStrutturata).includes(
					master.idDizionario
				)
			);
			if (!twinBindsBack) continue;

			pairs.push({ master, twin });
			takenTwins.add(twin.idAccessorio);
			break; // una vernice master ha un solo gemello
		}
	}

	return pairs;
}

/** Gli `idAccessorio` di tutti gli accessori referenziati nel sotto-albero. */
function subtreeAccessoryIds(
	node: MotornetFormula,
	all: WorkingAccessory[]
): number[] {
	return findByDizionarioIds(all, subtreeDizionarioIds(node)).map(
		(a) => a.idAccessorio
	);
}

/** Gli `idAccessorio` a cui si riferisce un ramo (una foglia o l'intero sotto-albero). */
function branchAccessoryIds(
	branch: OrBranchRef,
	all: WorkingAccessory[]
): number[] {
	return 'member' in branch
		? [branch.member.idAccessorio]
		: subtreeAccessoryIds(branch.node, all);
}

/** True se il ramo contiene almeno uno degli accessori scelti dall'utente. */
function branchHoldsPick(
	branch: OrBranchRef,
	all: WorkingAccessory[],
	picks: ReadonlySet<number>
): boolean {
	return branchAccessoryIds(branch, all).some((id) => picks.has(id));
}

/** Opzioni per {@link resolveFormulaAccessories}. */
export interface ResolveOptions {
	/**
	 * strict (default): un OR con più di un ramo praticabile non risolve NULLA
	 * finché una scelta non seleziona un ramo — nessun default silenzioso.
	 * Non-strict mantiene il vecchio comportamento (primo ramo per default);
	 * usato solo per la visualizzazione.
	 */
	strict?: boolean;
}

/**
 * Risolve una formula nell'insieme di accessori che essa determina, data la
 * risposta corrente ai nodi OR (`picks`). I nodi AND aggregano tutti i figli;
 * i nodi OR seguono un solo ramo — quello contenente una scelta dell'utente, o,
 * in modalità non-strict, il primo ramo per default (solo a fini di display).
 * In modalità strict un OR ambiguo non contribuisce nulla finché non è risolto.
 */
export function resolveFormulaAccessories(
	formula: MotornetFormula | null | undefined,
	all: WorkingAccessory[],
	picks: ReadonlySet<number> = new Set(),
	opts: ResolveOptions = {}
): WorkingAccessory[] {
	const strict = opts.strict !== false;
	const collected: WorkingAccessory[] = [];
	const seen = new Set<number>();
	if (formula) visit(formula);
	return collected;

	/** Aggiunge gli accessori al risultato, evitando i duplicati. */
	function collect(items: WorkingAccessory[]): void {
		for (const item of items) {
			if (seen.has(item.idAccessorio)) continue;
			seen.add(item.idAccessorio);
			collected.push(item);
		}
	}

	/** Visita un nodo: AND raccoglie tutto, OR segue il solo ramo attivo. */
	function visit(node: MotornetFormula): void {
		if (node.op !== OR) {
			collect(findByDizionarioIds(all, node.ids ?? []));
			for (const child of node.operations ?? []) visit(child);
			return;
		}

		const branches = orBranchesOf(node, all);
		if (branches.length === 0) return;

		let active = -1;
		if (branches.length === 1) {
			active = 0;
		} else {
			active = branches.findIndex((b) => branchHoldsPick(b, all, picks));
			if (active === -1 && !strict) active = 0;
		}
		if (active === -1) return;

		const branch = branches[active];
		if ('member' in branch) collect([branch.member]);
		else visit(branch.node);
	}
}

/**
 * Raccoglie i nodi OR di una formula che richiedono (o portano) una scelta
 * dell'utente, percorrendo solo il ramo attivo: i gruppi annidati dentro un
 * ramo non ancora scelto compaiono solo quando il ramo padre viene selezionato.
 *
 * `claimed` contiene gli accessori che appartengono già a una catena; il wizard
 * ufficiale li scarta da ogni lista di candidati prima di costruire i widget
 * (`!a.some(x => x.idAccessorio === c.idAccessorio && (x.accessorioRegola !=
 * null || x.isFirstOfChain))`). Senza questo filtro il capostipite di una catena
 * verrebbe proposto come risposta alle domande dei suoi stessi compagni.
 */
export function collectFormulaOrGroups(
	owner: WorkingAccessory,
	kind: OrChoice['kind'],
	formula: MotornetFormula | null | undefined,
	all: WorkingAccessory[],
	choices: OrChoice[],
	claimed: ReadonlySet<number> = new Set()
): FormulaOrGroup[] {
	if (!formula) return [];

	// solo i candidati vengono filtrati; il resolver continua a lavorare sull'insieme completo
	const visible = claimed.size
		? all.filter((a) => !claimed.has(a.idAccessorio))
		: all;

	const picks = new Set(
		choices
			.filter((c) => c.accessoryId === owner.idAccessorio && c.kind === kind)
			.map((c) => c.chosenId)
	);
	const groups: FormulaOrGroup[] = [];
	walk(formula, 'r');
	return groups;

	/**
	 * Percorre l'albero: gli AND scendono su tutti i figli; ogni OR con più rami
	 * praticabili diventa un gruppo (una domanda), e si prosegue solo lungo il
	 * ramo eventualmente già scelto.
	 */
	function walk(node: MotornetFormula, path: string): void {
		if (node.op !== OR) {
			(node.operations ?? []).forEach((child, i) =>
				walk(child, `${path}.${i}`)
			);
			return;
		}

		const branches = orBranchesOf(node, visible).filter(
			(b) => branchAccessoryIds(b, visible).length > 0
		);
		if (branches.length === 0) return;

		if (branches.length === 1) {
			const only = branches[0];
			if ('node' in only) walk(only.node, `${path}.0`);
			return;
		}

		const specs: FormulaOrBranch[] = branches.map((b) => {
			const subtreeIds = branchAccessoryIds(b, visible);
			if ('member' in b) {
				return { chosenId: b.member.idAccessorio, ids: subtreeIds };
			}
			const certain = resolveFormulaAccessories(b.node, visible, picks).map(
				(a) => a.idAccessorio
			);
			return {
				chosenId: subtreeIds[0],
				ids: certain.length > 0 ? certain : subtreeIds,
			};
		});

		const allIds = [
			...new Set(branches.flatMap((b) => branchAccessoryIds(b, visible))),
		];
		const chosenId = allIds.find((id) => picks.has(id));

		groups.push({
			accessoryId: owner.idAccessorio,
			kind,
			key: `${owner.idAccessorio}:${kind}:${path}`,
			branches: specs,
			allIds,
			chosenId,
		});

		const activeIndex = branches.findIndex((b) =>
			branchHoldsPick(b, visible, picks)
		);
		if (activeIndex >= 0) {
			const active = branches[activeIndex];
			if ('node' in active) walk(active.node, `${path}.${activeIndex}`);
		}
	}
}

/** True se l'accessorio ha almeno una formula (vincoli, inclusione o esclusione). */
export function hasFormula(accessory: WorkingAccessory): boolean {
	return (
		!!accessory.formulaVincoliStrutturata ||
		!!accessory.formulaInclusioneStrutturata ||
		!!accessory.formulaEsclusioneStrutturata
	);
}

/**
 * True se l'accessorio produce effetti di catena: ha una formula oppure prevede
 * l'esclusione logica di tutto il gruppo di appartenenza.
 */
const hasChainEffects = (accessory: WorkingAccessory): boolean =>
	hasFormula(accessory) || !!accessory.prevedeEsclusioneLogicaGruppo;

/** Seleziona un accessorio applicandone la catena, senza scelte OR esplicite. */
export function selectAccessory(
	accessory: WorkingAccessory,
	all: WorkingAccessory[]
): void {
	selectAccessoryWithChoices(accessory, all, []);
}

/** Deseleziona un accessorio, ripulendo tutto lo stato di catena da esso lasciato. */
export function deselectAccessory(accessory: WorkingAccessory): void {
	delete accessory.selected;
	delete accessory.tipoVincolo;
	delete accessory.tipoVincoloArray;
	delete accessory.appliedBy;
	delete accessory.appliedKind;
	delete accessory.prezzoDaVincolo;
	delete accessory.prezzoDaVincoloNetto;
}

/**
 * Commuta la selezione di un accessorio. Se ha una formula, non decide subito
 * ma restituisce `'modal'`: la UI deve aprire la finestra di conferma (che
 * mostra conseguenze e domande) prima di applicare la catena.
 */
export function toggleAccessory(
	accessory: WorkingAccessory,
	all: WorkingAccessory[]
): 'modal' | 'selected' | 'deselected' {
	if (accessory.selected) {
		deselectAccessory(accessory);
		return 'deselected';
	}
	if (hasFormula(accessory)) {
		return 'modal';
	}
	selectAccessory(accessory, all);
	return 'selected';
}

// Solo gli accessori già impegnati in una catena non sono disponibili come
// nuovi candidati. Gli accessori di serie NON sono pre-rivendicati: un optional
// può escludere — o trascinare — un accessorio di serie, esattamente come fa il
// configuratore ufficiale (un serie è candidato finché non è "timbrato" da una
// catena o non è capostipite di una catena).
const defaultClaims = (all: WorkingAccessory[]): Set<number> =>
	new Set(all.filter((a) => a.selected).map((a) => a.idAccessorio));

/**
 * Applica la catena innescata dalla selezione di un accessorio, mutando in loco
 * gli accessori di `all`.
 *
 * A partire dal capostipite (`accessory`) si propaga in ampiezza (BFS): per ogni
 * accessorio della catena si risolvono, date le scelte OR (`choices`), i suoi
 * - **vincoli** → compagni obbligati (`appliedKind: 'vincolo'`), che a loro volta
 *   propagano la propria catena completa; l'eventuale prezzo alternativo del
 *   vincolo viene applicato al capostipite;
 * - **inclusioni** → compagni inclusi a costo zero (`appliedKind: 'inclusione'`),
 *   di cui si valutano poi solo le esclusioni;
 * - **esclusioni** → accessori resi incompatibili (più l'esclusione logica di
 *   gruppo, se prevista).
 *
 * `claimed` tiene traccia degli accessori già "rivendicati" da una catena, così
 * che il primo che li reclama vinca i target contesi (l'ordine delle selezioni
 * conta). Ogni accessorio toccato riceve `tipoVincoloArray` (mappa id → effetto).
 */
export function selectAccessoryWithChoices(
	accessory: WorkingAccessory,
	all: WorkingAccessory[],
	choices: OrChoice[] = [],
	claimed: Set<number> = defaultClaims(all)
): void {
	const rootId = accessory.idAccessorio;
	accessory.selected = true;
	claimed.add(rootId);

	const queue: { item: WorkingAccessory; full: boolean }[] = [
		{ item: accessory, full: true },
	];
	const processed = new Set<number>();

	while (queue.length > 0) {
		const { item, full } = queue.shift()!;
		if (processed.has(item.idAccessorio)) continue;
		processed.add(item.idAccessorio);
		if (!hasChainEffects(item)) continue;

		const picksFor = (kind: OrChoice['kind']): Set<number> =>
			new Set(
				choices
					.filter(
						(c) => c.accessoryId === item.idAccessorio && c.kind === kind
					)
					.map((c) => c.chosenId)
			);

		const unclaimed = (items: WorkingAccessory[]): WorkingAccessory[] =>
			items.filter((a) => !claimed.has(a.idAccessorio));

		const vincolo = full
			? unclaimed(
					resolveFormulaAccessories(
						item.formulaVincoliStrutturata,
						all,
						picksFor('vincolo')
					)
			  )
			: [];
		if (full) applyVincoloAlternativePrice(item, all, picksFor('vincolo'));
		for (const companion of vincolo) {
			claimed.add(companion.idAccessorio);
			companion.selected = true;
			companion.appliedBy = rootId;
			companion.appliedKind = 'vincolo';
			queue.push({ item: companion, full: true });
		}

		const inclusione = full
			? unclaimed(
					resolveFormulaAccessories(
						item.formulaInclusioneStrutturata,
						all,
						picksFor('inclusione')
					)
			  )
			: [];
		for (const companion of inclusione) {
			claimed.add(companion.idAccessorio);
			companion.selected = true;
			companion.appliedBy = rootId;
			companion.appliedKind = 'inclusione';
			queue.push({ item: companion, full: false });
		}

		const esclusioneGruppo =
			item.prevedeEsclusioneLogicaGruppo && item.idDizionarioGruppo
				? all.filter(
						(a) =>
							a.idDizionarioGruppo === item.idDizionarioGruppo &&
							a.idAccessorio !== item.idAccessorio
				  )
				: [];
		const esclusione = unclaimed([
			...resolveFormulaAccessories(
				item.formulaEsclusioneStrutturata,
				all,
				picksFor('esclusione')
			),
			...esclusioneGruppo,
		]);
		for (const target of esclusione) claimed.add(target.idAccessorio);

		const vincoloArray: Record<string, MotornetTipoVincolo> = {};
		const assign = (items: WorkingAccessory[], kind: MotornetTipoVincolo) => {
			for (const a of items) vincoloArray[a.idAccessorio] = kind;
		};
		assign(esclusione, 'esclusione');
		assign(inclusione, 'inclusione');
		assign(vincolo, 'vincolo');

		item.tipoVincoloArray = vincoloArray;
		item.tipoVincolo = undefined;
		if (esclusione.length > 0) item.tipoVincolo = 'esclusione';
		if (inclusione.length > 0) item.tipoVincolo = 'inclusione';
		if (vincolo.length > 0) item.tipoVincolo = 'vincolo';
	}
}

/** Confronto strutturale profondo fra due formule (operatore, ids, sotto-alberi). */
const sameFormula = (
	a: MotornetFormula | null | undefined,
	b: MotornetFormula | null | undefined
): boolean => {
	if (!a || !b) return a === b;
	return (
		(a.op ?? null) === (b.op ?? null) &&
		JSON.stringify([...(a.ids ?? [])].sort()) ===
			JSON.stringify([...(b.ids ?? [])].sort()) &&
		(a.operations ?? []).length === (b.operations ?? []).length &&
		(a.operations ?? []).every((op, i) =>
			sameFormula(op, (b.operations ?? [])[i])
		)
	);
};

/**
 * Quando la formula dei vincoli ha risolto un ramo, l'alternativa corrispondente
 * in `formulaVincoli[]` fornisce il prezzo sostitutivo dell'accessorio.
 */
function applyVincoloAlternativePrice(
	item: WorkingAccessory,
	all: WorkingAccessory[],
	picks: ReadonlySet<number>
): void {
	const alternatives = item.formulaVincoli ?? [];
	const formula = item.formulaVincoliStrutturata;
	if (alternatives.length === 0 || !formula) return;

	const resolved = resolveFormulaAccessories(formula, all, picks);
	if (resolved.length === 0) return;

	let alternative: MotornetVincolo | undefined;
	if (alternatives.length === 1) {
		alternative = alternatives[0];
	} else {
		// l'alternativa di base non porta formula: non c'è nulla con cui
		// confrontarla ed è il prezzo di ripiego, non uno guidato dai vincoli.
		alternative = alternatives.find(
			(v) =>
				!!v.formulaStrutturata &&
				subtreeAccessoryIds(v.formulaStrutturata, all).some((id) =>
					picks.has(id)
				)
		);
		if (!alternative && formula.op === OR) {
			const branches = orBranchesOf(formula, all);
			const active =
				branches.length === 1
					? branches[0]
					: branches.find((b) => branchHoldsPick(b, all, picks));
			if (active && 'node' in active) {
				alternative = alternatives.find((v) =>
					sameFormula(v.formulaStrutturata, active.node)
				);
			}
		}
	}
	if (!alternative) return;

	item.prezzoDaVincolo = alternative.prezzo;
	item.prezzoDaVincoloNetto = vincoloNetPrice(item, alternative.prezzo);
}

/**
 * L'alternativa porta un solo prezzo (listino lordo). Il netto si ricava dal
 * rapporto netto/lordo dell'accessorio stesso quando è utilizzabile, altrimenti
 * scorporando l'IVA di default — lo stesso ripiego che la UI ufficiale usa per
 * mostrare i prezzi netti.
 */
function vincoloNetPrice(item: WorkingAccessory, prezzo: number): number {
	const { prezzoListino, prezzoListinoNetto } = item;
	// prezzoListino puo' essere null nei payload reali: il confronto lo scarta
	// gia' a runtime, si tiene solo il ripiego sull'IVA di default
	if (prezzoListino != null && prezzoListino > 0 && prezzoListinoNetto > 0) {
		return Number(((prezzo * prezzoListinoNetto) / prezzoListino).toFixed(2));
	}
	return Number((prezzo / (1 + IVA_FALLBACK / 100)).toFixed(2));
}

/**
 * Fonde i `tipoVincoloArray` di tutti gli accessori selezionati in un'unica
 * mappa id → effetto: per ogni accessorio dice se la configurazione corrente lo
 * vincola, lo include o lo esclude.
 */
export function collectVincoloMap(
	all: WorkingAccessory[]
): Record<string, MotornetTipoVincolo> {
	const result: Record<string, MotornetTipoVincolo> = {};
	for (const a of all) {
		if (a.selected && a.tipoVincoloArray) {
			Object.assign(result, a.tipoVincoloArray);
		}
	}
	return result;
}

/** Id di tutti gli accessori "timbrati" da una catena (vincolati/inclusi/esclusi). */
export function disabledIds(all: WorkingAccessory[]): number[] {
	return Object.keys(collectVincoloMap(all))
		.map(Number)
		.filter(Number.isInteger);
}

/**
 * Somma il prezzo degli accessori selezionati. I compagni di inclusione costano
 * 0; un accessorio con un'alternativa di vincolo scelta usa quel prezzo al posto
 * del proprio (che può essere il segnaposto -1). Gli accessori di serie non
 * concorrono al totale.
 */
export function totalAccessoriesPrice(
	all: WorkingAccessory[],
	priceField: 'prezzoListino' | 'prezzoListinoNetto' = 'prezzoListino'
): number {
	let total = 0;

	for (const accessory of all) {
		if (!accessory.selected) continue;
		if (accessory.tipoAccessorio === 'serie') continue;
		if (accessory.appliedKind === 'inclusione') continue;

		const alternative =
			priceField === 'prezzoListino'
				? accessory.prezzoDaVincolo
				: accessory.prezzoDaVincoloNetto;
		const price = alternative ?? accessory[priceField];
		if (price) {
			total += price;
		}
	}

	return total;
}

/** Conta gli accessori selezionati per ciascun bucket. */
export function selectedCounts(
	all: WorkingAccessory[]
): Record<MotornetAccessoryBucket, number> {
	const counts: Record<MotornetAccessoryBucket, number> = {
		serie: 0,
		optionals: 0,
		vernici: 0,
		pacchetti: 0,
		accessoriVinNonCorrispondenti: 0,
	};

	for (const accessory of all) {
		if (!accessory.selected) continue;
		counts[accessory.tipoAccessorio]++;
	}

	return counts;
}

/**
 * Costruisce da zero una configurazione completa a partire da un payload e da un
 * elenco di `idDizionario` scelti: applica le relative catene (con le scelte OR),
 * quindi calcola selezionati, id disabilitati, raggruppamenti per macrogruppo,
 * conteggi e totali (accessori + prezzo veicolo). Punto d'ingresso "one-shot"
 * usato quando si ricostruisce lo stato senza mantenere un'istanza di engine.
 */
export function buildConfiguration(
	response: MotornetAccessoriesResponse,
	selectedIds: number[],
	vehiclePrice = 0,
	choices: OrChoice[] = []
): ConfigurationResult {
	const accessories = flattenAccessories(response);
	const claimed = defaultClaims(accessories);

	const uniqueIds = [...new Set(selectedIds.filter((id) => Number.isInteger(id)))];
	for (const idDizionario of uniqueIds) {
		const accessory = accessories.find((a) => a.idDizionario === idDizionario);
		if (accessory && accessory.tipoAccessorio !== 'serie') {
			selectAccessoryWithChoices(accessory, accessories, choices, claimed);
		}
	}

	const selected = accessories.filter((a) => a.selected);
	const disabled = disabledIds(accessories);

	const byMacrogruppo = {
		serie: {},
		optionals: {},
		vernici: {},
		pacchetti: {},
		accessoriVinNonCorrispondenti: {},
	} as ConfigurationResult['byMacrogruppo'];
	for (const kind of ACCESSORY_BUCKETS) {
		byMacrogruppo[kind] = groupByMacrogruppo(
			accessories.filter((a) => a.tipoAccessorio === kind)
		);
	}

	const totalListino = totalAccessoriesPrice(accessories, 'prezzoListino');
	const totalPrice = totalAccessoriesPrice(accessories, 'prezzoListinoNetto');

	return {
		accessories,
		selected,
		disabledIds: disabled,
		totalAccessoriesPriceListino: totalListino,
		totalAccessoriesPrice: totalPrice,
		totalVehiclePrice: vehiclePrice + totalListino,
		counts: selectedCounts(accessories),
		byMacrogruppo,
	};
}
